import { defineStore } from "pinia"
import {
  CategoryOption, LevelOption,
  PLSession,
  SubjectSummary,
  SubjectOption, StatementSelectionsData, LevelGroup, SavingState, AlignedGoals, AlignedGoalsResponse
} from '@/presentlevels/presentlevels_types.ts'
import { PL_SUMMARY_SAVING_STATES } from '@/presentlevels/presentlevels_constants.ts'
import {
  getSubjectCategoriesWithSkills, getLevelOptions, getPresentlevelsSession,
  getSubjectOptions,
  postCategorySelections,
  getSubjectSummariesForSession, postPresentlevelsSession, postSubjectSummary, putSummaryStatementText, getSubjectSummaryByUUID, deleteSubjectSummary, putSubjectSummary
} from "@/presentlevels/api_v2.ts";
import { getGoalByUUID } from "@/goals/api.ts";
import { Goal } from "@/goals/goal_types.ts";
import { deleteFolderResource } from '@/library/api'
import { HTTPError } from '@/util/http.ts'
import { Resource } from '@/resources/resource_types'
import { getResources } from '@/resources/api'
import { getAllAssessmentUUIDs } from "@/presentlevels/assessmentResourceUtil";
import { useVisibilityStore } from '@/visibility/visibility.store'
import { useLibraryStore } from '@/library/library.store.ts'

export const usePresentLevelsStore = defineStore('presentLevels', {
  state: () => ({
    plSession: null as PLSession | null,
    subjectSummaries: [] as SubjectSummary[],
    loadingSubjectSummaries: true,
    generatingStatements: {} as Record<string, boolean>,
    loadingAlignedGoals: {} as Record<string, boolean>,
    generateGoalsError: {} as Record<string, boolean>,
    subjectOptions: [] as SubjectOption[],
    loadingSubjects: true,
    levelOptions: [] as LevelOption[],
    loadingLevels: true,
    categoryOptions: {} as { [subjectUUID: string]: CategoryOption[] },
    selectedSkillsMap: {
      strengths: {} as Record<string, boolean>,
      needs: {} as Record<string, boolean>,
    },
    resourcesByUUID: {} as Record<string, Resource>,
    errorDisplay: null as null | { displayHeader: string, displayBody: string },
    savingStates: {} as Record<string, SavingState>,
    savingStateTimeouts: {} as Record<string, ReturnType<typeof setTimeout>>,
  }),
  actions: {
    async fetchSessionDetails(sessionUUID: string) {
      await Promise.all([
        this.fetchSession(sessionUUID),
        this.fetchSessionSummaries(sessionUUID)
          .then(() => {
            return Promise.all([
              this.fetchCategoriesForSummarySubjects(),
              useLibraryStore().fetchFoldersForPLSubjectSummaries()
            ])
          }),
      ])
    },
    async fetchSession(sessionUUID: string) {
      try {
        this.plSession = await getPresentlevelsSession(sessionUUID)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async createSubjectSummaries(args: { studentName: string, gradeUUID: string, subjectUUIDs: string[], sessionUUID: string }) {
      for (const subjectUUID of args.subjectUUIDs) {
        try {
          this.subjectSummaries.push(await postSubjectSummary({
            student_name: args.studentName,
            subject_uuid: subjectUUID,
            subject_label: this.getSubjectLabelByUUID(subjectUUID),
            grade_uuid: args.gradeUUID,
            grade_label: this.getGradeLabelByUUID(args.gradeUUID),
            pl_session_uuid: args.sessionUUID
          }))
        } catch (e) {
          await this.setErrorDisplay(e)
        }
      }
    },
    async createSession(args: { studentName: string, gradeUUID: string, subjectUUIDs: string[] }) {
      try {
        this.plSession = await postPresentlevelsSession({
          student_name: args.studentName,
          enrolled_grade_uuid: args.gradeUUID,
          selected_subject_uuids: args.subjectUUIDs
        })
      } catch (e) {
        await this.setErrorDisplay(e)
        return
      }
      await this.createSubjectSummaries({
        studentName: args.studentName,
        gradeUUID: args.gradeUUID,
        subjectUUIDs: args.subjectUUIDs,
        sessionUUID: this.plSession.uuid
      })
    },
    async fetchSessionSummaries(sessionUUID: string) {
      try {
        this.loadingSubjectSummaries = true
        const summaries = await getSubjectSummariesForSession(sessionUUID)
        this.subjectSummaries = summaries
        this.loadingSubjectSummaries = false
        await this.syncResourcesWithCurrentSummaries()
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },

    async syncResourcesWithCurrentSummaries() {
      const statementTexts = this.subjectSummaries.map(s => s.statement_text)
      const currentUUIDs = getAllAssessmentUUIDs(statementTexts)

      const resourcesToKeep: Record<string, Resource> = {}
      const uuidsToFetch: string[] = []

      for (const uuid of currentUUIDs) {
        if (this.resourcesByUUID[uuid]) {
          resourcesToKeep[uuid] = this.resourcesByUUID[uuid]
        } else {
          uuidsToFetch.push(uuid)
        }
      }

      const updatedResourcesMap = { ...resourcesToKeep }

      if (uuidsToFetch.length > 0) {
        const newResources = await this.fetchResourcesForAssessmentUUIDs(uuidsToFetch)
        newResources.forEach(resource => {
          updatedResourcesMap[resource.uuid] = resource
        })
      }

      this.resourcesByUUID = updatedResourcesMap
    },

    async fetchSummaryByUUID(summaryUUID: string) {
      try {
        this.loadingSubjectSummaries = true
        const summary = await getSubjectSummaryByUUID(summaryUUID)
        this.loadingSubjectSummaries = false
        this.subjectSummaries = [summary]
        await this.syncResourcesWithCurrentSummaries()
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async fetchSubjectOptions() {
      const visibilityStore = useVisibilityStore()

      this.loadingSubjects = true
      try {
        this.subjectOptions = await getSubjectOptions()
        if (!visibilityStore.selVisibility) {
          // Replace "Social & Emotional Learning" with "Social Skills" when SEL visibility is disabled
          this.subjectOptions = this.subjectOptions.map(subject => {
            if (subject.label === 'Social & Emotional Learning') {
              return { ...subject, label: 'Social Skills' }
            }
            return subject
          })
        }
        this.loadingSubjects = false
      } catch (e) {
        await this.setErrorDisplay(e)
        this.loadingSubjects = false
        throw e
      }
    },
    async fetchLevelOptions() {
      this.loadingLevels = true
      try {
        this.levelOptions = await getLevelOptions()
        this.loadingLevels = false
      } catch (e) {
        await this.setErrorDisplay(e)
        this.loadingLevels = false
        throw e
      }
    },
    async fetchCategoriesForSummarySubjects() {
      const promises = []
      for (const subjectUUID of this.summarySubjectUUIDs) {
        promises.push(this.fetchCategoriesForSubject(subjectUUID))
      }
      try {
        await Promise.all(promises)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async fetchCategoriesForSubject(subjectUUID: string) {
      try {
        this.categoryOptions[subjectUUID] = await getSubjectCategoriesWithSkills(subjectUUID)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async fetchResourcesForAssessmentUUIDs(uuids: string[]): Promise<Resource[]> {
      try {
        return await getResources(uuids)
      } catch (err) {
        console.error('Failed to fetch resources:', err)
        return []
      }
    },
    getSubjectLabelByUUID(subjectUUID: string) {
      const subject = this.subjectOptions.find(subject => subject.uuid === subjectUUID)
      return subject ? subject.label : ''
    },
    getGradeLabelByUUID(gradeUUID: string) {
      const grade = this.levelOptions.find(grade => grade.uuid === gradeUUID)
      return grade ? grade.label : ''
    },
    async setErrorDisplay(error: unknown) {
      const showGenericError = (!(error instanceof Error) || !error?.cause)
      if (showGenericError) {
        this.errorDisplay = {
          displayHeader: 'Error',
          displayBody: 'An error occurred'
        }
        return
      }

      this.errorDisplay = {
        displayHeader: error.message,
        displayBody: error.message
      }

      const httpErrorResponse = (error as HTTPError).response || (error.cause as HTTPError).response
      if (!httpErrorResponse) {
        return
      }

      let errorResponseMessage;
      try {
        const errorResponseJSON = await httpErrorResponse.json()
        errorResponseMessage = errorResponseJSON.error
      } catch {
        return
      }
      if (errorResponseMessage) {
        this.errorDisplay.displayBody += `: ${errorResponseMessage}`
      }
    },
    clearErrorDisplay() {
      this.errorDisplay = null
    },
    async submitStatementSelections(args: StatementSelectionsData) {
      this.generatingStatements[args.summaryUUID] = true
      this.generateGoalsError[args.summaryUUID] = false
      try {
        const updatedSummary = await postCategorySelections(args)
        this.generateGoalsError[args.summaryUUID] = updatedSummary.aligned_goals_generation_error || false

        // Remove the transient flag so it doesn't get stored
        const cleaned: SubjectSummary = { ...(updatedSummary as SubjectSummary) }
        delete cleaned.aligned_goals_generation_error

        await this.updateSummary(cleaned)
      } finally {
        this.generatingStatements[args.summaryUUID] = false
      }
    },
    async updateSubjectSummary(summaryUUID: string, fieldsToUpdate: Partial<SubjectSummary>) {
      try {
        const updatedSummary = await putSummaryStatementText(summaryUUID, fieldsToUpdate)
        await this.updateSummary(updatedSummary)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    clearSavingStateTimeout(summaryUUID: string) {
      if (this.savingStateTimeouts[summaryUUID]) {
        clearTimeout(this.savingStateTimeouts[summaryUUID])
        delete this.savingStateTimeouts[summaryUUID]
      }
    },
    setSavingState(summaryUUID: string, state: SavingState) {
      this.clearSavingStateTimeout(summaryUUID)
      this.savingStates[summaryUUID] = state

      if (state === PL_SUMMARY_SAVING_STATES.JUST_SAVED) {
        this.savingStateTimeouts[summaryUUID] = setTimeout(() => {
          this.savingStates[summaryUUID] = PL_SUMMARY_SAVING_STATES.NO_CHANGES
          delete this.savingStateTimeouts[summaryUUID]
        }, 3000)
      }
    },
    async saveSubjectSummary(
      summaryUUID: string,
      fieldsToUpdate: Partial<SubjectSummary>
    ): Promise<void> {
      if (!summaryUUID) return

      this.setSavingState(summaryUUID, PL_SUMMARY_SAVING_STATES.SAVING)
      try {
        await this.updateSubjectSummary(summaryUUID, fieldsToUpdate)
        this.setSavingState(summaryUUID, PL_SUMMARY_SAVING_STATES.JUST_SAVED)
      } catch (error) {
        this.setSavingState(summaryUUID, PL_SUMMARY_SAVING_STATES.UNSAVED_CHANGES)
        await this.setErrorDisplay(error)
        throw error
      }
    },
    async deleteStatement(summaryUUID: string) {
      const libraryStore = useLibraryStore()
      try {
        const libraryFolder = libraryStore.getFolderByContentUuid(summaryUUID)
        if (libraryFolder) {
          await deleteFolderResource(libraryFolder.uuid, summaryUUID, 'pl_student_summary')
        }
        await deleteSubjectSummary(summaryUUID)
        this.subjectSummaries = this.subjectSummaries.filter(summary => summary.uuid !== summaryUUID)
        delete this.savingStates[summaryUUID]

        this.clearSavingStateTimeout(summaryUUID)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async updateSummary(summary: SubjectSummary) {
      this.subjectSummaries = this.subjectSummaries.map((s) => {
        if (s.uuid === summary.uuid) {
          return summary
        }
        return s
      })

      await this.syncResourcesWithCurrentSummaries()
    },
    getSelectedStrengthUUIDs() {
      return this.getSelectedSkillUUIDsByType('strengths')
    },
    getSelectedNeedUUIDs() {
      return this.getSelectedSkillUUIDsByType('needs')
    },
    getSelectedSkillUUIDsByType(type: 'strengths' | 'needs'): string[] {
      const skillsByType = this.selectedSkillsMap[type]
      return Object.keys(skillsByType).filter((skillUUID) => skillsByType[skillUUID])
    },
    getCategoriesForSubject(subjectUUID: string): CategoryOption[] {
      return this.categoryOptions[subjectUUID] || []
    },
    resetSelectedSkills() {
      this.selectedSkillsMap = {
        strengths: {},
        needs: {}
      }
    },
    getLevelledSelectedSkillLabelsFromGroups(type: 'strengths' | 'needs', levelGroups: LevelGroup[]): Record<string, string[]> {
      const result: Record<string, string[]> = {}
      for (const levelGroup of levelGroups) {
        const selectedSkills = levelGroup.skills.filter((skill) => this.selectedSkillsMap[type][skill.uuid])
        if (selectedSkills.length) {
          result[levelGroup.label] = selectedSkills.map((skill) => skill.label)
        }
      }
      return result
    },
    async fetchAlignedGoalsForSubjectSummary(summaryUUID: string): Promise<AlignedGoals> {
      try {
        const summary = await getSubjectSummaryByUUID(summaryUUID)
        return summary.aligned_goals
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      }
    },
    async fetchGoalDetailsByUUID(goalUUIDs: string[]): Promise<Goal[]> {
      const goalPromises = goalUUIDs.map(async (goalUuid) => {
        try {
          return await getGoalByUUID(goalUuid)
        } catch (error) {
          // Silently skip invalid/deleted goals - don't raise error
          console.warn(`Skipping invalid goal ${goalUuid}`, error)
          return null
        }
      })
      const results = await Promise.all(goalPromises)
      // Filter out null values (invalid goals)
      return results.filter((goal): goal is Goal => goal !== null)
    },

    async convertGoalUUIDsToGoalObjects(alignedGoalsData: AlignedGoalsResponse): Promise<AlignedGoals> {
      const alignedGoals: AlignedGoals = []

      for (const [categoryLabel, categoryData] of Object.entries(alignedGoalsData)) {
        // Skip categories with null or undefined goals
        if (!categoryData.goals) {
          continue
        }
        const fullGoalObjects = await this.fetchGoalDetailsByUUID(categoryData.goals)
        // Only add category if it has valid goals
        if (fullGoalObjects.length > 0) {
          alignedGoals.push({ [categoryLabel]: fullGoalObjects })
        }
      }

      return alignedGoals
    },

    mergeAlignedGoalsWithDeduplication(existingAlignedGoals: AlignedGoals, newAlignedGoals: AlignedGoals): AlignedGoals {
      const categoryGoalsMap = new Map<string, Goal[]>()

      const addGoalsToMap = (goals: Goal[], categoryLabel: string) => {
        if (categoryGoalsMap.has(categoryLabel)) {
          const existingGoals = categoryGoalsMap.get(categoryLabel)!
          const existingUUIDs = new Set(existingGoals.map(goal => goal.uuid))
          const newUniqueGoals = goals.filter(goal => !existingUUIDs.has(goal.uuid))
          categoryGoalsMap.get(categoryLabel)!.push(...newUniqueGoals)
        } else {
          // Remove duplicates within the goals array itself
          const uniqueGoals = goals.filter((goal, index, arr) =>
            arr.findIndex(g => g.uuid === goal.uuid) === index
          )
          categoryGoalsMap.set(categoryLabel, uniqueGoals)
        }
      }

      for (const domainGoals of existingAlignedGoals) {
        for (const [categoryLabel, goals] of Object.entries(domainGoals)) {
          addGoalsToMap(goals, categoryLabel)
        }
      }

      for (const domainGoals of newAlignedGoals) {
        for (const [categoryLabel, goals] of Object.entries(domainGoals)) {
          addGoalsToMap(goals, categoryLabel)
        }
      }

      // Convert map back to AlignedGoals format
      const mergedAlignedGoals: AlignedGoals = []
      for (const [categoryLabel, goals] of categoryGoalsMap.entries()) {
        mergedAlignedGoals.push({ [categoryLabel]: goals })
      }

      return mergedAlignedGoals
    },

    async persistAlignedGoalsToDatabase(summaryUUID: string, alignedGoals: AlignedGoals): Promise<SubjectSummary> {
      return await putSubjectSummary(summaryUUID, { aligned_goals: alignedGoals })
    },

    async fetchAndPersistAlignedGoalsForSummary(summaryUUID: string, alignedGoalsData: AlignedGoalsResponse) {
      this.loadingAlignedGoals[summaryUUID] = true
      try {
        // Check if alignedGoalsData has any goals - exit early if not
        const hasGoals = Object.values(alignedGoalsData).some(categoryData => 
          categoryData.goals && categoryData.goals.length > 0
        )
        if (!hasGoals) {
          return
        }

        const newAlignedGoals = await this.convertGoalUUIDsToGoalObjects(alignedGoalsData)

        const existingAlignedGoals = this.getAlignedGoalsForSummary(summaryUUID) || []
        const mergedAlignedGoals = this.mergeAlignedGoalsWithDeduplication(existingAlignedGoals, newAlignedGoals)

        const updatedSummary = await this.persistAlignedGoalsToDatabase(summaryUUID, mergedAlignedGoals)

        await this.updateSummary(updatedSummary)
      } catch (e) {
        await this.setErrorDisplay(e)
        throw e
      } finally {
        this.loadingAlignedGoals[summaryUUID] = false
      }
    },
  },
  getters: {
    summarySubjectUUIDs: (state) => {
      return Array.from(new Set(state.subjectSummaries.map((summary: SubjectSummary) => summary.subject_uuid)))
    },
    gradeOptions: (state) => {
      return state.levelOptions.filter((level: LevelOption) => level.level_type === 'academic')
    },
    enrolledGradeLabel: (state) => {
      if (!state.plSession) return ''

      const gradeOption = state.levelOptions.find(option => option.uuid === state.plSession?.enrolled_grade_uuid)
      return gradeOption?.label || ''
    },
    getAlignedGoalsForSummary: (state) => (summaryUUID: string) => {
      return state.subjectSummaries.find(summary => summary.uuid === summaryUUID)?.aligned_goals || []
    },
    getGradeLabelForSummary: (state) => (summaryUUID: string) => {
      return state.subjectSummaries.find(summary => summary.uuid === summaryUUID)?.grade_label || ''
    },
    isStatementGeneratingForSummaryUUID: (state) => (summaryUUID: string) => {
      return state.generatingStatements[summaryUUID] || false
    },
    isLoadingAlignedGoalsForSummaryUUID: (state) => (summaryUUID: string) => {
      return state.loadingAlignedGoals[summaryUUID] || false
    }
  }
})